import React, { useEffect, useState } from 'react';
import { calculateAge } from '@this/domain/age_util';
import type TravelerInformation from '@this/src/domain/traveler_information';
import moment from 'moment';
import type { Moment } from '@this/src/lib/moment';
import { observer } from 'mobx-react';
import { FormField, FormTitle, InputGroup, TextInput } from './order_item_traveler.style';

type Props = {
  travelerInformation: TravelerInformation;
  transportDate: Moment | null;
  hiddenPersonalForm?: boolean;
  classNameForModifiedField: (...path: (string | number)[]) => string;
};

const OrderItemTravelerDomesticAirFormComponent: React.FC<Props> = ({
  travelerInformation,
  transportDate,
  hiddenPersonalForm,
  classNameForModifiedField
}) => {
  const [birthdayInput, setBirthdayInput] = useState(
    travelerInformation.flightBirthday?.format('YYYY-MM-DD') ?? ''
  );

  useEffect(() => {
    const formatted = travelerInformation.flightBirthday?.format('YYYY-MM-DD') ?? '';
    setBirthdayInput(formatted);
  }, [travelerInformation.flightBirthday]);

  const handleBirthdayChange = (value: string) => {
    setBirthdayInput(value);
  };

  const handleBirthdayBlur = () => {
    if (birthdayInput === '') {
      travelerInformation.flightBirthday = undefined;
      return;
    }

    const parsed = moment(birthdayInput, 'YYYY-MM-DD', true);
    if (parsed.isValid()) {
      travelerInformation.flightBirthday = parsed;
      setBirthdayInput(parsed.format('YYYY-MM-DD'));
      return;
    }

    travelerInformation.flightBirthday = undefined;
  };

  return (
    <div>
      <FormTitle>航空券情報</FormTitle>
      {!hiddenPersonalForm && (
        <>
          <FormField>
            <label
              className={`
                ${classNameForModifiedField('flightLastName')}
                ${classNameForModifiedField('flightFirstName')}
              `}
            >
              氏名(カタカナ)
            </label>
            <InputGroup>
              <TextInput
                type="text"
                placeholder="姓(カタカナ)"
                value={travelerInformation.flightLastName}
                onChange={e => {
                  travelerInformation.flightLastName = e.target.value;
                }}
              />
              <TextInput
                type="text"
                placeholder="名(カタカナ)"
                value={travelerInformation.flightFirstName}
                onChange={e => {
                  travelerInformation.flightFirstName = e.target.value;
                }}
              />
            </InputGroup>
          </FormField>
          <FormField>
            <label
              className={`
                ${classNameForModifiedField('flightLastNameRoman')}
                ${classNameForModifiedField('flightFirstNameRoman')}
              `}
            >
              氏名(ローマ字)
            </label>
            <InputGroup>
              <TextInput
                type="text"
                placeholder="姓(ローマ字)"
                value={travelerInformation.flightLastNameRoman}
                onChange={e => {
                  travelerInformation.flightLastNameRoman = e.target.value;
                }}
              />
              <TextInput
                type="text"
                placeholder="名(ローマ字)"
                value={travelerInformation.flightFirstNameRoman}
                onChange={e => {
                  travelerInformation.flightFirstNameRoman = e.target.value;
                }}
              />
            </InputGroup>
          </FormField>
          <FormField>
            <label className={classNameForModifiedField('flightBirthday')}>誕生日</label>
            <TextInput
              placeholder="例) 2020-04-01"
              value={birthdayInput}
              onChange={e => handleBirthdayChange(e.target.value)}
              onBlur={handleBirthdayBlur}
            />
            {travelerInformation.flightBirthday && transportDate && (
              <div style={{ marginBottom: '0.75em' }}>
                <label>搭乗時年齢</label>
                <span>{`${calculateAge(travelerInformation.flightBirthday, transportDate).toString()}歳`}</span>
              </div>
            )}
          </FormField>
          <FormField>
            <label className={classNameForModifiedField('flightGender')}>性別</label>
            <InputGroup>
              <label>
                <input
                  type="radio"
                  value="male"
                  name="foreign-air-gender"
                  checked={travelerInformation.flightGender === 'male'}
                  onChange={() => {
                    travelerInformation.flightGender = 'male';
                  }}
                />
                男性
              </label>
              <label>
                <input
                  type="radio"
                  value="female"
                  name="foreign-air-gender"
                  checked={travelerInformation.flightGender === 'female'}
                  onChange={() => {
                    travelerInformation.flightGender = 'female';
                  }}
                />
                女性
              </label>
            </InputGroup>
          </FormField>
        </>
      )}

      <FormField>
        <label className={classNameForModifiedField('flightTel')}>緊急連絡先番号</label>
        <TextInput
          placeholder="例) 09012345678"
          value={travelerInformation.flightTel ?? ''}
          onChange={e => {
            travelerInformation.flightTel = e.target.value;
          }}
        />
      </FormField>
      <FormField>
        <label className={classNameForModifiedField('airSeat')}>座席の希望</label>
        <TextInput
          placeholder="例) 前方通路側"
          value={travelerInformation.airSeat ?? ''}
          onChange={e => {
            travelerInformation.airSeat = e.target.value;
          }}
        />
      </FormField>
      <FormField>
        <label className={classNameForModifiedField('mileageNumber')}>マイル番号</label>
        <TextInput
          placeholder="例) 0123456789"
          value={travelerInformation.mileageNumber ?? ''}
          onChange={e => {
            travelerInformation.mileageNumber = e.target.value;
          }}
        />
      </FormField>
    </div>
  );
};

const OrderItemTravelerDomesticAirForm = observer(OrderItemTravelerDomesticAirFormComponent);
export default OrderItemTravelerDomesticAirForm;
